import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Rocket, CheckCircle, XCircle, Clock, RotateCcw } from 'lucide-react';

interface Deployment {
  id: string;
  status: string;
  triggeredBy: string;
  commitSha: string | null;
  commitMsg: string | null;
  branch: string | null;
  createdAt: string;
  finishedAt: string | null;
  duration: number | null;
  site: {
    id: string;
    name: string;
    domain: string;
  };
}

export default function Deployments() {
  const [deployments, setDeployments] = useState<Deployment[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('');

  useEffect(() => {
    const params = new URLSearchParams();
    if (filter) params.set('status', filter);

    fetch(`/api/deployments?${params.toString()}`)
      .then((r) => r.json())
      .then((data) => {
        setDeployments(data.deployments || []);
        setLoading(false);
      });
  }, [filter]);

  const rollback = async (deploymentId: string) => {
    if (!confirm('Are you sure you want to rollback to this deployment?')) return;

    await fetch(`/api/deployments/${deploymentId}/rollback`, { method: 'POST' });

    // Refresh list
    const res = await fetch('/api/deployments');
    const data = await res.json();
    setDeployments(data.deployments || []);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'SUCCESS':
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'FAILED':
        return <XCircle className="w-5 h-5 text-red-400" />;
      case 'RUNNING':
        return <Rocket className="w-5 h-5 text-blue-400 animate-pulse" />;
      default:
        return <Clock className="w-5 h-5 text-yellow-400" />;
    }
  };

  const statusColors: Record<string, string> = {
    SUCCESS: 'text-green-400 bg-green-400/10',
    FAILED: 'text-red-400 bg-red-400/10',
    RUNNING: 'text-blue-400 bg-blue-400/10',
    PENDING: 'text-yellow-400 bg-yellow-400/10',
    CANCELLED: 'text-gray-400 bg-gray-400/10',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold text-white">Deployments</h1>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="">All Status</option>
          <option value="SUCCESS">Success</option>
          <option value="FAILED">Failed</option>
          <option value="RUNNING">Running</option>
          <option value="PENDING">Pending</option>
        </select>
      </div>

      <div className="bg-gray-800 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-700">
              <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">
                Status
              </th>
              <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">
                Site
              </th>
              <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">
                Commit
              </th>
              <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">
                Triggered By
              </th>
              <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">
                Duration
              </th>
              <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">
                Time
              </th>
              <th className="text-right px-6 py-4 text-sm font-medium text-gray-400">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {deployments.map((deployment) => (
              <tr key={deployment.id} className="hover:bg-gray-700/50">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    {getStatusIcon(deployment.status)}
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-medium ${
                        statusColors[deployment.status]
                      }`}
                    >
                      {deployment.status}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <Link
                    to={`/sites/${deployment.site.id}`}
                    className="text-white hover:text-primary-400"
                  >
                    {deployment.site.name}
                  </Link>
                  <p className="text-sm text-gray-500">{deployment.site.domain}</p>
                </td>
                <td className="px-6 py-4">
                  {deployment.commitSha ? (
                    <div>
                      <code className="text-sm text-gray-300">
                        {deployment.commitSha.substring(0, 7)}
                      </code>
                      {deployment.commitMsg && (
                        <p className="text-sm text-gray-500 truncate max-w-xs">
                          {deployment.commitMsg}
                        </p>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-500">-</span>
                  )}
                </td>
                <td className="px-6 py-4 text-gray-300">{deployment.triggeredBy}</td>
                <td className="px-6 py-4 text-gray-300">
                  {deployment.duration ? `${deployment.duration}s` : '-'}
                </td>
                <td className="px-6 py-4 text-gray-400 text-sm">
                  {new Date(deployment.createdAt).toLocaleString()}
                </td>
                <td className="px-6 py-4 text-right">
                  {deployment.status === 'SUCCESS' && (
                    <button
                      onClick={() => rollback(deployment.id)}
                      className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-gray-700"
                      title="Rollback to this deployment"
                    >
                      <RotateCcw className="w-4 h-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {deployments.length === 0 && (
          <div className="text-center py-12">
            <Rocket className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">
              No deployments found
            </h3>
            <p className="text-gray-400">
              Deployments will appear here when you push to connected repos
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
